import React, { useState } from 'react';
import styles from './WorkshopPaymentModal.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
// Use the actual file present in public/ (QE_code.jpg)
const qrImageSrc = `${process.env.PUBLIC_URL || ''}/QE_code.jpg`;

export default function WorkshopPaymentModal({ onClose, onComplete, registrationData, registrationId }) {
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setPaymentScreenshot(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!paymentScreenshot) {
      alert('Please upload payment screenshot');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('paymentScreenshot', paymentScreenshot);
      formData.append('registrationId', registrationId);

      const response = await fetch(`${API_URL}/api/workshop/upload-payment`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        onComplete({
          paymentScreenshot: screenshotPreview,
          paymentDate: new Date().toISOString(),
          paymentAmount: 99
        });
      } else {
        alert(data.message || 'Failed to upload payment. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Payment upload error:', error);
      alert('Failed to upload payment. Please check your internet connection and try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          ✕
        </button>

        <div className={styles.modalHeader}>
          <h2>Complete Your Payment</h2>
          <p>Scan the QR code below to make payment</p>
        </div>

        <div className={styles.modalBody}>
          {/* Payment Details */}
          <div className={styles.paymentInfo}>
            <div className={styles.amountBox}>
              <span className={styles.amountLabel}>Amount to Pay</span>
              <span className={styles.amountValue}>₹99.00</span>
            </div>
          </div>

          {/* QR Code Section */}
          <div className={styles.qrSection}>
            <h3>Scan QR Code to Pay</h3>
            <div className={styles.qrCodeContainer}>
              <div className={styles.qrCodeCard}>
                <img
                  src={qrImageSrc}
                  alt="Rustic Yarns workshop UPI QR"
                  className={styles.qrCodeImage}
                />
                <p className={styles.qrNote}>📱 Scan with any UPI app</p>
              </div>
            </div>

            <div className={styles.paymentOptions}>
              <p className={styles.orText}>OR pay using UPI ID</p>
              <div className={styles.upiIdBox}>
                <span className={styles.upiId}>rusticyarns@upi</span>
                <button 
                  className={styles.copyButton}
                  onClick={() => {
                    navigator.clipboard.writeText('rusticyarns@upi');
                    alert('UPI ID copied to clipboard!');
                  }}
                >
                  📋 Copy
                </button>
              </div>
            </div>
          </div>

          {/* Screenshot Upload Section */}
          <div className={styles.uploadSection}>
            <h3>Upload Payment Screenshot</h3>
            <p className={styles.uploadNote}>
              After making payment, please upload a screenshot for verification
            </p>

            <div className={styles.fileUploadArea}>
              {!screenshotPreview ? (
                <label htmlFor="screenshot" className={styles.uploadLabel}>
                  <div className={styles.uploadIcon}>📸</div>
                  <span>Click to upload screenshot</span>
                  <span className={styles.uploadHint}>PNG, JPG (Max 5MB)</span>
                  <input
                    type="file"
                    id="screenshot"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={styles.fileInput}
                  />
                </label>
              ) : (
                <div className={styles.previewContainer}>
                  <img 
                    src={screenshotPreview} 
                    alt="Payment Screenshot" 
                    className={styles.screenshotPreview}
                  />
                  <button 
                    className={styles.removeButton}
                    onClick={() => {
                      setPaymentScreenshot(null);
                      setScreenshotPreview(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Payment Instructions */}
          <div className={styles.instructions}>
            <h4>Payment Instructions:</h4>
            <ol>
              <li>Open any UPI app (GPay, PhonePe, Paytm, etc.)</li>
              <li>Scan the QR code or enter the UPI ID</li>
              <li>Pay ₹99.00</li>
              <li>Take a screenshot of the payment confirmation</li>
              <li>Upload the screenshot above and click Submit</li>
            </ol>
          </div>

          {/* Submit Button */}
          <button 
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={!paymentScreenshot || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit & Complete Registration'}
          </button>

          <div className={styles.disclaimer}>
            <p>⚠️ Your registration will be confirmed after payment verification. You will receive workshop details via email and WhatsApp within 24 hours.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
